import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, BadgeCheck, IndianRupee, ShieldCheck, Wrench } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { LogoPill } from "@/components/BrandWall";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BRANDS } from "@/data/site";
import { BRAND_DETAILS } from "@/data/brands";

const ASSURANCES = [
  { icon: BadgeCheck, title: "Genuine Billing", desc: "Official channel stock with proper GST invoice" },
  { icon: ShieldCheck, title: "Full Brand Warranty", desc: "Manufacturer warranty honoured at our counter" },
  { icon: Wrench, title: "Installation Support", desc: "Coordination with your plumber till the last fitting" },
  { icon: IndianRupee, title: "Project Pricing", desc: "BOQ-wise quotes for architects & developers" },
];

export default function BrandPage() {
  const { brandId } = useParams();
  const brand = BRANDS.find((b) => b.id === brandId);
  const details = BRAND_DETAILS[brandId];

  usePageMeta(
    details?.seo.title || "SRK Bath and Tiles — Faridabad",
    details?.seo.description || "Bath fittings and tiles showroom in Greater Faridabad."
  );

  if (!brand || !details) return <Navigate to="/brands" replace />;

  const others = BRANDS.filter((b) => b.id !== brandId && BRAND_DETAILS[b.id]);
  const waText = encodeURIComponent(`Hi, I'm interested in ${brand.name} products. Please share today's best prices.`);

  return (
    <div data-testid={`brand-page-${brandId}`}>
      <section className="relative overflow-hidden pb-16 pt-40 sm:pt-52">
        <div className="pointer-events-none absolute -right-40 top-24 h-96 w-96 rounded-full bg-brand/5 blur-3xl" />
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <LogoPill name={brand.name} logo={brand.logos[0]?.logo} className="!px-6 !py-3" />
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-8 font-mono text-xs uppercase tracking-[0.35em] text-brand"
          >
            Authorised Dealer · Sector 86, Greater Faridabad
          </motion.p>
          <h1 className="mt-6 font-serif text-5xl font-medium leading-[1.05] tracking-tight text-foreground sm:text-6xl lg:text-7xl" data-testid="brand-page-headline">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.3, ease: [0.215, 0.61, 0.355, 1] }}
              >
                {brand.name} <em className="text-brand-gradient not-italic">at SRK.</em>
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-[#5C6570] sm:text-lg"
          >
            {brand.blurb}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to={`/contact?brand=${encodeURIComponent(brand.name)}`}
              data-testid="brand-page-enquire-button"
              className="group flex items-center gap-3 bg-brand px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-all duration-300 hover:bg-brand-bright"
            >
              Enquire About {brand.name}
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href={`https://wa.me/919873628088?text=${waText}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brand-page-whatsapp-button"
              className="border border-ink/25 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:border-brand hover:text-brand"
            >
              WhatsApp for Prices
            </a>
          </motion.div>
        </div>
      </section>

      <section data-testid="brand-page-stock" className="bg-cream py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal className="mb-12">
            <Eyebrow>What We Stock</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-serif text-2xl font-medium leading-tight tracking-tight text-foreground sm:text-3xl lg:text-4xl">
              The {brand.name} range, <em className="text-brand-gradient not-italic">on our floor.</em>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {details.categories.map((c, i) => (
              <Reveal key={c.name} delay={(i % 4) * 0.07}>
                <div
                  data-testid={`brand-category-${i}`}
                  className="group h-full border border-ink/10 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-brand/50"
                >
                  <span className="font-serif text-2xl font-medium text-brand/30 transition-colors duration-300 group-hover:text-brand">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-3 font-serif text-lg font-semibold text-foreground">{c.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#5C6570]">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            {details.gallery.map((img, i) => (
              <Reveal key={img} delay={i * 0.1}>
                <div className="img-spotlight group overflow-hidden border border-ink/10">
                  <img
                    src={img}
                    alt={`${brand.name} bathroom fittings Faridabad showroom display ${i + 1}`}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="brand-page-assurance" className="border-y border-brand/30 bg-ink py-14 sm:py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 sm:grid-cols-2 sm:px-8 lg:grid-cols-4 lg:px-16">
          {ASSURANCES.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.08}>
              <div data-testid={`assurance-${i}`} className="flex gap-4">
                <a.icon size={24} className="mt-1 shrink-0 text-brand-bright" />
                <div>
                  <h3 className="font-serif text-lg font-semibold text-[#F6F1E7]">{a.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-[#9AA6B2]">{a.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="brand-page-seo" className="py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          <Reveal>
            <Eyebrow>{brand.name} in Faridabad</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl font-medium leading-tight tracking-tight text-foreground sm:text-3xl">
              Authorised {brand.name} showroom in <em className="text-brand-gradient not-italic">Greater Faridabad.</em>
            </h2>
            {details.seoContent.map((p, i) => (
              <p key={i} className="mt-6 text-base leading-relaxed text-[#5C6570]">{p}</p>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>Also On Our Floor</Eyebrow>
              <h2 className="mt-4 font-serif text-2xl font-medium tracking-tight text-foreground sm:text-3xl">
                Explore more brands
              </h2>
            </div>
            <div className="flex flex-wrap gap-4">
              {others.map((o) => (
                <Link
                  key={o.id}
                  to={`/brands/${o.id}`}
                  data-testid={`brand-page-next-${o.id}`}
                  className="group flex items-center gap-2 border border-ink/15 px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:border-brand hover:text-brand"
                >
                  {o.name} <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Link>
              ))}
              <Link
                to="/brands"
                data-testid="brand-page-all-brands"
                className="group flex items-center gap-2 border border-brand/60 px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-brand transition-all duration-300 hover:bg-brand hover:text-paper"
              >
                All Brands <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
